/* eslint-disable tsdoc/syntax */
import type { SetStateAction } from 'react';
import { useCallback, useState } from 'react';

export type UseHtmlElementReturn = [
  HTMLElement | null,
  (el: HTMLElement | null) => void,
];

/**
 * Obtain a reference to an underlying HTML element in a stateful manner. This
 * can be preferred to useRef for instances where an html node may not be
 * available on initial render but you would like to receive updates when it is
 * available.
 *
 * @remarks
 * Consider alternatives to this hook. See
 * {@link https://app.shortcut.com/farmersdog/story/101439/review-of-usehtmlelement | SC-101439}
 * for more details.
 * @example Const [divElement, divRef] = useHtmlElement(); return <div
 * ref={divRef}></div>
 */
export function useHtmlElement(): UseHtmlElementReturn {
  const [currentHtmlElement, setCurrentHtmlElement] =
    useState<HTMLElement | null>(null);

  const setRef = useCallback((node: SetStateAction<HTMLElement | null>) => {
    if (node !== null) {
      setCurrentHtmlElement(node);
    }
  }, []);

  return [currentHtmlElement, setRef];
}
